import { useEffect, useState } from "react";
import { Loader2, Sparkles, Tag } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import ProductCard from "../components/common/ProductCard";
import { formatGBP } from "../components/common/StockBadge";
import AgeBadge from "../components/common/AgeBadge";

import api from "../lib/api";

export default function Offers() {
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    document.title = "Offers & Bundles · Clacton Fireworks";
    api.get("/offers").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
    return () => { document.title = "Clacton Fireworks"; };
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="offers-page">
      <SectionTitle eyebrow="Deals" title="Offers & Bundles"
        subtitle="Every live promotion, grouped by deal. Discounts apply automatically at checkout." />

      {/* One age-restriction notice for the whole page (not repeated inside cards) */}
      <div className="mt-4">
        <AgeBadge size={38} linked />
      </div>

      {!groups ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : groups.length === 0 ? (
        <div className="mt-12 text-center text-white/60" data-testid="offers-empty">
          <Tag className="h-8 w-8 mx-auto text-gold/50" />
          <p className="mt-3">There are no live offers right now — please check back soon.</p>
        </div>
      ) : (
        <div className="mt-10 grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 items-start" data-testid="offers-grid">
          {groups.map((g) => (
            <GlassCard key={g.promotion_id} hover={false} className="p-5 flex flex-col" >
              <section id={g.slug} data-testid={`offer-group-${g.slug}`} className="scroll-mt-24 flex flex-col h-full">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gold text-ink px-3 py-1 text-sm font-bold uppercase tracking-wide">
                    <Sparkles className="h-4 w-4" /> {g.ribbon}
                  </span>
                  {g.ends_label && <span className="text-xs text-white/50">Ends {g.ends_label}</span>}
                </div>
                {g.description && <p className="mt-2 text-sm text-white/70">{g.description}</p>}

                {g.type === "fixed_bundle" && (
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-gold/25 bg-gold/5 px-4 py-2.5" data-testid={`bundle-summary-${g.slug}`}>
                    <span className="text-xs text-white/60">Normal <span className="line-through">{formatGBP(g.normal_total)}</span></span>
                    <span className="font-playfair text-lg text-emerald-400">Bundle {formatGBP(g.bundle_price)}</span>
                    {g.saving > 0 && <span className="text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">Save {formatGBP(g.saving)}</span>}
                  </div>
                )}

                <div className="mt-5 grid gap-3 grid-cols-2">
                  {(g.products || []).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
                </div>
              </section>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
